import { addDays, format, isSameDay, startOfDay } from "date-fns";
import { enUS, es, fr } from "date-fns/locale";
import { CalendarDays, ChevronRight } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";

import { Skeleton } from "@/components/ui/skeleton";
import { formatTime } from "@/lib/dates";
import { cn } from "@/lib/utils";

import { TaskKindBadge } from "../tasks/TaskKindBadge";
import { UPCOMING_DAYS_AHEAD, upcomingDays } from "./upcomingModel";
import {
  SOURCE_BAR,
  useAgendaEntries,
  type AgendaEntry,
} from "./useAgendaEntries";

const dateFnsLocale = (locale: string) =>
  locale.startsWith("fr") ? fr : locale.startsWith("en") ? enUS : es;

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/** One compact line: time, colour bar, title (and the internal badge). */
const UpcomingRow = ({
  entry,
  locale,
}: {
  entry: AgendaEntry;
  locale: string;
}) => {
  const translate = useTranslate();
  const body = (
    <>
      <span
        className={cn(
          "w-11 shrink-0 text-muted-foreground tabular-nums",
          entry.allDay ? "text-[11px] leading-[1.15]" : "text-[13px]",
        )}
      >
        {entry.allDay
          ? translate("crm.mobile.all_day")
          : formatTime(entry.start, locale)}
      </span>
      <span
        aria-hidden
        className={cn(
          "h-5 w-1 shrink-0 rounded-full",
          SOURCE_BAR[entry.source] ?? "bg-chart-1",
        )}
      />
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-[15px]",
          entry.done && "text-muted-foreground line-through",
        )}
      >
        {entry.title}
      </span>
      {entry.internal ? <TaskKindBadge /> : null}
      {entry.href ? (
        <ChevronRight
          aria-hidden
          className="size-4 shrink-0 text-muted-foreground/70"
        />
      ) : null}
    </>
  );
  const className =
    "upcoming-row flex min-h-11 items-center gap-2.5 px-3.5 py-2 outline-none focus-visible:bg-muted";
  return (
    <li>
      {entry.href ? (
        <Link to={entry.href} className={className}>
          {body}
        </Link>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  );
};

/**
 * The top-bar calendar's quick look: today and the next few days with
 * something on them, as compact grouped rows, and a way into the full
 * calendar (month / week / agenda views) for anything beyond that.
 * `onOpenCalendar` leaves for the calendar page (and closes the window).
 */
export const UpcomingAgenda = ({
  onOpenCalendar,
}: {
  onOpenCalendar: () => void;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const dfLocale = dateFnsLocale(locale);
  const today = useMemo(() => startOfDay(new Date()), []);
  const { entries, pending } = useAgendaEntries(
    today,
    addDays(today, UPCOMING_DAYS_AHEAD),
  );
  const days = useMemo(() => upcomingDays(entries, today), [entries, today]);
  const t = (key: string) => translate(`crm.mobile.upcoming.${key}`);

  const dayTitle = (day: Date) => {
    const date = format(day, "EEEE d MMM", { locale: dfLocale });
    if (isSameDay(day, today)) return `${t("today")} · ${date}`;
    if (isSameDay(day, addDays(today, 1))) return `${t("tomorrow")} · ${date}`;
    return capitalize(date);
  };

  return (
    <div className="flex flex-col gap-4 pb-4">
      <button
        type="button"
        onClick={onOpenCalendar}
        className="liquid-glass glass-press flex h-11 items-center gap-2.5 self-stretch rounded-full px-4 text-[15px] font-medium text-[var(--glass-accent)] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <CalendarDays aria-hidden className="size-[1.125rem]" strokeWidth={2} />
        <span className="flex-1 text-left">{t("see_calendar")}</span>
        <ChevronRight aria-hidden className="size-4" strokeWidth={2.25} />
      </button>
      {pending ? (
        <Skeleton className="h-32 w-full rounded-[22px]" />
      ) : (
        days.map(({ day, key, entries: dayEntries }) => (
          <section key={key} className="flex flex-col gap-1.5">
            <h3 className="px-3.5 text-[13px] font-semibold tracking-wide text-muted-foreground uppercase">
              {dayTitle(day)}
            </h3>
            {dayEntries.length > 0 ? (
              <ul className="divide-y divide-border/60 overflow-hidden rounded-[22px] bg-[var(--settings-card)] ring-1 ring-border/60">
                {dayEntries.map((entry) => (
                  <UpcomingRow key={entry.id} entry={entry} locale={locale} />
                ))}
              </ul>
            ) : (
              <p className="px-3.5 text-sm text-muted-foreground">
                {days.length === 1 ? t("empty") : t("today_empty")}
              </p>
            )}
          </section>
        ))
      )}
    </div>
  );
};
